/*
 * Lazy-loading variant of behaviors.ts. Loads page-specific behavior modules
 * on demand via lazyDefine() instead of eagerly importing them all.
 *
 * The server selects this entry point instead of behaviors.ts when the
 * behaviors_lazy_define feature flag is enabled.
 *
 * Every-page modules are still statically imported (bundled into the entry chunk).
 * Page-specific modules are loaded as separate chunks only when their trigger
 * element appears in the DOM.
 */

import './github/behaviors/ajax-error'
import './github/behaviors/ajax-loading'
import './github/behaviors/analytics-events'
import './github/behaviors/animated-image'
import './github/behaviors/batch-deferred-content'
import './github/behaviors/clear-data-on-logout'
import './github/behaviors/code-nav'
import './github/behaviors/confirm'
import './github/behaviors/details'
import './github/behaviors/details-element'
import './github/behaviors/disable-with'
import './github/behaviors/favicon'
import './github/behaviors/fixed-offset-fragment-navigation-observer'
import './github/behaviors/flash'
import './github/behaviors/flash-observer'
import './github/behaviors/focus-delay'
import './github/behaviors/hash-change'
import './github/behaviors/header'
import './github/behaviors/hotkey-map'
import './github/behaviors/hydro-analytics-pageview'
import './github/behaviors/include-fragment'
import './github/behaviors/indeterminate'
import './github/behaviors/notification-shelf-height-observer'
import './github/behaviors/poll-include-fragment'
import './github/behaviors/print-popup'
import './github/behaviors/register-service-worker'
import './github/behaviors/remote-form'
import './github/behaviors/removed-contents'
import './github/behaviors/replace-remote-form'
import './github/behaviors/responsive-underlinenav'
import './github/behaviors/session-resume'
import './github/behaviors/side-navigation'
import './github/behaviors/site-notifications'
import './github/behaviors/site-search'
import './github/behaviors/size-to-fit'
import './github/behaviors/snippet-clipboard-copy'
import './github/behaviors/social'
import './github/behaviors/socket-channel'
import './github/behaviors/sso-auto-replay'
import './github/behaviors/stale-session'
import './github/behaviors/sticky-header-height-observer'
import './github/behaviors/targetable-elements'
import './github/behaviors/timeline/marker'
import './github/behaviors/timeline/progressive'
import './github/behaviors/timeline/timeline-accessibility'
import './github/behaviors/toggle-stuck'
import './github/behaviors/turbo-timing'
import './github/behaviors/tz-cookie'
import './github/behaviors/unread-comments'
import './github/behaviors/unread-item-counter'
import './github/behaviors/updatable-content-observer'
import './github/behaviors/updatable-content-stale-object'
import './github/behaviors/user-content'
import './github/behaviors/user-select-contain-polyfill'
import './github/behaviors/warn-unsaved-changes'
import './github/behaviors/will-transition-once'
import './github/issues/issue-link'
import './github/behaviors/clearable-input'
import '@github-ui/turbo'
import {setup as setupSoftNav} from '@github-ui/soft-nav/setup'
import {setupCPUCookie} from '@github-ui/cpu-cookie'

import {lazyDefine} from '@github/catalyst'

setupSoftNav()
setupCPUCookie()

// Initialize web-vitals asynchronously to avoid blocking hydration
;(async () => {
  const {setupWebVitals} = await import('@github-ui/web-vitals/setup')
  setupWebVitals()
})()

// Page-specific behaviors — loaded on demand when trigger elements appear in the DOM.
// Each import() creates a separate chunk that only loads on pages that need it.
lazyDefine({
  '.js-csv-filter-field': () => import('./github/behaviors/blob-csv'),
  '.js-blob-header': () => import('./github/repositories/blob-header'),
  '.js-billing-section, .js-org-signup-duration-change': () => import('./github/behaviors/billing-addons'),
  '.js-branch-protection-integration-select': () => import('./github/behaviors/branch-protection-integration-select'),
  '.js-bulk-actions-container': () => import('./github/behaviors/bulk-actions'),
  '.js-characters-remaining-field': () => import('./github/behaviors/characters-remaining'),
  '.js-check-all-container': () => import('./github/behaviors/check-all'),
  '.js-commits-list-item': () => import('./github/behaviors/commits'),
  '.js-company-name-input': () => import('./github/behaviors/company-name-input'),
  '.js-company-owned': () => import('./github/behaviors/company-owned'),
  'details-menu': () => import('./github/behaviors/details-menu'),
  'button[data-show-dialog-id], summary[data-show-dialog-id]': () => import('./github/behaviors/dialog-helpers'),
  '.js-feature-preview-indicator-container': () => import('./github/behaviors/feature-preview-indicator'),
  '[data-feature-preview-trigger-url]': () => import('./github/behaviors/feature-preview-trigger'),
  'file-attachment, [data-file-attachment-for]': () => import('./github/behaviors/file-attachment'),
  'fuzzy-list': () => import('./github/behaviors/fuzzy-list'),
  '.js-hook-url-field, .js-hook-event-choice, .js-redeliver-hook-form': () => import('./github/behaviors/hooks'),
  '.js-length-limited-input': () => import('./github/behaviors/length-limited-input-with-warning'),
  '.js-member-search-filter': () => import('./github/behaviors/member-search-filter'),
  '.js-permission-menu-form': () => import('./github/behaviors/permission-menu-form'),
  '.js-render-target': () => import('./github/behaviors/render-editor'),
  '.js-render-needs-enrichment': () => import('./github/behaviors/enrichable-markdown-render'),
  '.js-highlight-code-snippet-columns': () => import('./github/behaviors/scanning'),
  '.readme-edit': () => import('./github/behaviors/readme-task-toggle'),
  '.js-ajax-pagination': () => import('./github/behaviors/ajax-pagination'),
  '.js-immediate-updates': () => import('./github/behaviors/immediate-updates'),
  'a.btn.disabled': () => import('./github/behaviors/buttons'),
  'text-expander[data-emoji-url]': () => import('./github/behaviors/suggesters/emoji-suggester'),
  'text-expander[data-issue-url]': () => import('./github/behaviors/suggesters/issue-suggester'),
  'text-expander[data-mention-url]': () => import('./github/behaviors/suggesters/mention-suggester'),

  // Event-driven behaviors — could be further optimized to load on first
  // relevant user interaction instead of on element display.
  '[data-hovercard-url]': () => import('./github/behaviors/hovercards'),
  '[data-octo-click]': () => import('./github/behaviors/hydro-analytics-tracking'),
  '[data-hydro-click]': () => import('./github/behaviors/hydro-click-tracking'),
  '[data-copy-feedback]': () => import('./github/behaviors/clipboard-copy'),
  '.js-quick-submit': () => import('./github/behaviors/quick-submit'),
  '.js-comment-quote-reply': () => import('./github/behaviors/quote-selection'),
  '.js-reactions-container': () => import('./github/behaviors/reactions'),
  'auto-complete': () => import('./github/behaviors/autocomplete'),
  '[data-autoselect]': () => import('./github/behaviors/autoselect'),
  'form[data-autosubmit]': () => import('./github/behaviors/autosubmit'),
  'auto-check': () => import('./github/behaviors/autocheck'),
  '[data-autosearch-results-container]': () => import('./github/behaviors/autosearch-form'),
  '.js-new-comment-form, .errored.js-remove-error-state-on-click': () => import('./github/behaviors/commenting/ajax'),
  '.js-comment-and-button': () => import('./github/behaviors/commenting/close'),
  '.js-comment-header-actions-deferred-include-fragment, .js-comment-edit-button, .js-comment-hide-button': () =>
    import('./github/behaviors/commenting/edit'),
  '.js-comment-field': () => import('./github/behaviors/commenting/focus'),
  '.js-paste-markdown': () => import('./github/behaviors/commenting/markdown'),
  '.js-write-tab': () => import('./github/behaviors/commenting/preview'),
  '#new_comment_field': () => import('./github/behaviors/commenting/reaction-suggestion'),
  'form.js-encrypt-submit': () => import('./github/behaviors/encrypt'),
  '.email-hidden-toggle': () => import('./github/behaviors/gfm'),
  'filter-input': () => import('./github/behaviors/filter-input'),
  '.js-filterable-field': () => import('./github/behaviors/filterable'),
  '.js-form-toggle-target': () => import('./github/behaviors/form-toggle'),
  '[data-required-value]': () => import('./github/behaviors/html-validation'),
  '.js-keyboard-shortcuts, .js-modifier-key': () => import('./github/behaviors/keyboard-shortcuts'),
  '.js-smoothscroll-anchor': () => import('./github/behaviors/smooth-scroll-anchor'),
  '[data-sudo-required]': () => import('./github/behaviors/sudo-required'),
  'tab-container': () => import('./github/behaviors/tab-container'),
  '.js-tag-input-container': () => import('./github/behaviors/tag-input'),
  '.js-toggler-container': () => import('./github/behaviors/toggler'),
  '.js-build-status': () => import('./github/behaviors/statuses'),
  '.js-team-mention': () => import('./github/behaviors/team-members'),
  '.js-sync-select-menu-text': () => import('./github/behaviors/sync-details-text'),
  '.js-notice-dismiss, .js-notice-dismiss-remote': () => import('./github/behaviors/notice'),
  '.js-permalink-shortcut': () => import('./github/behaviors/permalink'),
  '.js-github-dev-shortcut, .js-github-dev-new-tab-shortcut': () => import('./github/behaviors/open-in-github-dev'),
  '.js-saved-reply-menu': () => import('./github/behaviors/saved-replies'),
  '.js-upload-avatar-image': () => import('./github/behaviors/upload-avatar'),
  '.js-user-status-container, .user-status-dialog-fragment': () => import('./github/behaviors/user-status-loader'),
  '.js-survey-contact-checkbox': () => import('./github/behaviors/survey'),
  'sudo-credential-options': () => import('./github/behaviors/sudo-credential-options'),
  '.js-task-list-container, .contains-task-list': () => import('./github/behaviors/task-list'),
  '.js-label-filter-field, .js-new-label-color-input': () => import('./github/issues/labels'),
  '.js-discussion-sidebar-menu': () => import('./github/issues/sidebar'),
  '.js-user-list-form, .js-user-list-menu, .js-follow-list': () => import('./github/behaviors/user-lists'),
  '#clear-project-search-button': () => import('./github/memex/search'),
})
